import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import AutoSizeTable, { TableProps } from 'Component/common/auto-size-table';
import { Button, Input, Popconfirm, Space, Switch } from 'udesk-ui';
import { Wrapper, Header, Title, Extra } from './components/styled-components';
import Filed from './components/Field';
import { Enable } from './components/Field/const';
import { TablePaginationConfig } from 'udesk-ui/lib/table';
import { getTopicSettings } from 'src/api/topicSettings';
import { deleteTopicSettingsById } from 'src/api/topicSettings/{id}';
import { putTopicSettingsByIdStatusByStatus } from 'src/api/topicSettings/{id}/status/{status}';
import { GetTopicSettingsQuery, TopicSettingFoundResponse } from 'src/api/types';

interface TemplateProps {
    type?: number;
    taskId?: number;
    lang?: number;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { type, taskId, lang } = props;

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: 0,
        current: 1,
        pageSize: 10,
        showSizeChanger: true,
    });

    const [dataSource, setDataSource] = useState<TopicSettingFoundResponse[]>([]);

    const { loading: getLoading, run } = useRequest(
        ({
            pageNum,
            pageSize,
            topicName,
        }: Pick<GetTopicSettingsQuery, 'pageNum' | 'pageSize' | 'topicName'> = {}) =>
            getTopicSettings({
                params: {
                    taskId: taskId!,
                    pageNum: pageNum ?? pagination.current,
                    pageSize: pageSize ?? pagination.pageSize,
                    topicName,
                },
            }),
        {
            ready: !!taskId,
            onSuccess: (res) => {
                setDataSource(res.data || []);
                setPagination((pagination) => {
                    return {
                        ...pagination,
                        total: res.paging?.total ?? 0,
                        current: res.paging?.pageNum ?? 1,
                        pageSize: res.paging?.pageSize ?? 0,
                    };
                });
            },
        }
    );

    const onChange: TableProps<TopicSettingFoundResponse>['onChange'] = (
        paginate,
        filters,
        sorter,
        { action }
    ) => {
        if (action === 'paginate') {
            run({
                pageNum: paginate.current,
                pageSize: paginate.pageSize,
            });
        }
    };

    const [putLoading, setPutLoading] = useState(false);

    const onEnableChange = useCallback(
        (id?: number, isEnable?: boolean) => {
            if (id) {
                setPutLoading(true);
                putTopicSettingsByIdStatusByStatus({
                    segments: {
                        id,
                        status: isEnable ? Enable.OPEN : Enable.CLOSE,
                    },
                })
                    .then(() => {
                        run();
                    })
                    .finally(() => {
                        setPutLoading(false);
                    });
            }
        },
        [run]
    );

    const [delLoading, setDelLoading] = useState(false);

    const onDel = useCallback(
        (id?: number) => {
            if (id) {
                setDelLoading(true);
                deleteTopicSettingsById({ segments: { id } })
                    .then(() => {
                        run();
                    })
                    .finally(() => {
                        setDelLoading(false);
                    });
            }
        },
        [run]
    );

    const [fieldId, setFieldId] = useState<number>();
    const [isEdit, setIsEdit] = useState(false);

    const onAdd = () => {
        setFieldId(undefined);
        setIsEdit(true);
    };

    const onEdit = (id?: number) => {
        setFieldId(id);
        setIsEdit(true);
    };

    const onBack = () => {
        setIsEdit(false);
        run();
    };

    const columns = useMemo<TableProps<TopicSettingFoundResponse>['columns']>(() => {
        return [
            {
                title: /* 话题名称 */ UdeskLocales['current'].pages.tasks.manage.template
                    .automaticAdd.topicAnalysis.index.topicName,
                dataIndex: 'topicName',
                key: 'topicName',
            },
            {
                title: /* 话题描述 */ UdeskLocales['current'].pages.tasks.manage.template
                    .automaticAdd.topicAnalysis.index.topicDescription,
                dataIndex: 'topicDesc',
                key: 'topicDesc',
                render: (v, r) => {
                    return v || '-';
                },
            },
            {
                title: /* 是否启用 */ UdeskLocales['current'].pages.tasks.manage.template
                    .automaticAdd.businessSummary.index.enabled,
                dataIndex: 'topicStatus',
                key: 'topicStatus',
                render: (v, r) => {
                    return (
                        <Switch
                            checked={r.topicStatus === Enable.OPEN}
                            onChange={(c) => onEnableChange(r.id, c)}
                        />
                    );
                },
            },
            {
                title: /* 话题标签 */ UdeskLocales['current'].pages.tasks.manage.template
                    .automaticAdd.topicAnalysis.index.topicTag,
                dataIndex: 'tagGroupName',
                key: 'tagGroupName',
            },
            {
                title: /* 操作 */ UdeskLocales['current'].pages.tasks.manage.template.automaticAdd
                    .businessSummary.index.operation,
                key: 'action',
                render: (v, r) => {
                    return (
                        <Space>
                            <Button type="link" style={{ padding: 0 }} onClick={() => onEdit(r.id)}>
                                {/* 编辑 */}
                                {
                                    UdeskLocales['current'].pages.tasks.manage.template.automaticAdd
                                        .businessSummary.index.edit
                                }
                            </Button>
                            <Popconfirm
                                title={
                                    /* 确定删除吗？ */ UdeskLocales['current'].pages.tasks.manage
                                        .template.automaticAdd.businessSummary.index
                                        .areYouSureToDeleteIt
                                }
                                onConfirm={() => onDel(r.id)}
                            >
                                <Button type="link" danger style={{ padding: 0 }}>
                                    {/* 删除 */}
                                    {
                                        UdeskLocales['current'].pages.tasks.manage.template
                                            .automaticAdd.businessSummary.index.delete
                                    }
                                </Button>
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ];
    }, [onDel, onEnableChange]);

    const onSearch = (v: string) => {
        run({ topicName: v, pageNum: 1 });
    };

    return (
        <Wrapper>
            {!isEdit ? (
                <>
                    <Header>
                        <Title>
                            <Input.Search
                                onSearch={onSearch}
                                placeholder={
                                    /* 请输入话题名称 */ UdeskLocales['current'].pages.tasks.manage
                                        .template.automaticAdd.topicAnalysis.index
                                        .pleaseEnterATopicName
                                }
                            />
                        </Title>

                        <Extra>
                            <Space>
                                <Button type="link" onClick={onAdd}>{/* 增加话题 */}{UdeskLocales['current'].pages.tasks.manage.template.automaticAdd.topicAnalysis.index.addTopic}</Button>
                            </Space>
                        </Extra>
                    </Header>

                    <AutoSizeTable
                        columns={columns}
                        dataSource={dataSource}
                        loading={getLoading || putLoading || delLoading}
                        pagination={pagination}
                        onChange={onChange}
                    />
                </>
            ) : (
                <Filed type={type} onBack={onBack} id={fieldId} taskId={taskId} lang={lang} />
            )}
        </Wrapper>
    );
});

export default Template;
